/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import {
  Close,
  PersonSearchOutlined,
  PersonAddAlt1Outlined,
  CheckCircleOutline,
  BackspaceOutlined,
  DialpadOutlined,
  SearchOffOutlined,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  closeCustomerDialog,
  selectCustomer,
  setSelectedCustomer,
} from "../../Store/Slices/customerSlice";
import { createPhoneOrder } from "../../Store/Slices/restaurantSlice";
import {
  useLazyLookupCustomerQuery,
  useCreateCustomerMutation,
} from "../../Store/Services/CustomerService";
import styles from "./CustomerDialog.module.css";

const EMAIL_REGEX = /^[a-zA-Z0-9+._-]+@[a-zA-Z0-9.-]+\.[a-z]+$/;
const MOBILE_REGEX = /^[0-9+][0-9\s-]{6,14}$/;

const emptyForm = {
  custName: "",
  custMobile: "",
  custEmail: "",
  state: "",
  area: "",
  building: "",
  floor: "",
  apartment: "",
  addressLine1: "",
  addressLine2: "",
  landmark: "",
};

const toCreateCustomerPayload = (customerForm) => {
  return {
    custName: String(customerForm.custName || "").trim(),
    custEmail: String(customerForm.custEmail || "").trim(),
    custMobile: String(customerForm.custMobile || "").trim(),
    state: String(customerForm.state || "").trim(),
    area: String(customerForm.area || "").trim(),
    building: String(customerForm.building || "").trim(),
    floor: String(customerForm.floor || "").trim(),
    apartment: String(customerForm.apartment || "").trim(),
    addressLine1: String(customerForm.addressLine1 || "").trim(),
    addressLine2: String(customerForm.addressLine2 || "").trim(),
    landmark: String(customerForm.landmark || "").trim(),
  };
};

// Views: "search" (enter mobile/email), "select" (pick from multiple matches),
// "notfound" (no match, offer create), "found" (single match), "create" (new customer form)
const CustomerDialog = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isDialogOpen, purpose } = useSelector(selectCustomer);

  const [identifier, setIdentifier] = useState("");
  const [view, setView] = useState("search");
  const [form, setForm] = useState(emptyForm);
  const [matches, setMatches] = useState([]);
  const [searched, setSearched] = useState(false);
  const [showDialpad, setShowDialpad] = useState(true);

  const [lookupCustomer, { isFetching: isSearching }] =
    useLazyLookupCustomerQuery();
  const [createCustomer, { isLoading: isCreating }] =
    useCreateCustomerMutation();

  const resetAndClose = () => {
    setIdentifier("");
    setView("search");
    setForm(emptyForm);
    setMatches([]);
    setSearched(false);
    setShowDialpad(true);
    dispatch(closeCustomerDialog());
  };

  const handleDialpadPress = (key) =>
    setIdentifier((prev) => (prev + key).slice(0, 30));

  const handleDialpadBackspace = () =>
    setIdentifier((prev) => prev.slice(0, -1));

  const handleDialpadClear = () => setIdentifier("");

  const isMobile = (value) => MOBILE_REGEX.test(value.trim());
  const isEmail = (value) => EMAIL_REGEX.test(value.trim());

  // Number of typed characters required before we auto-search:
  // 4 digits for a mobile number, or the first 4 letters of an email.
  const meetsThreshold = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (/@/.test(trimmed) || /[a-zA-Z]/.test(trimmed)) {
      return trimmed.replace(/\s/g, "").length >= 4;
    }
    return trimmed.replace(/\D/g, "").length >= 4;
  };

  // Live search: debounce the lookup as the cashier types / taps the dial pad.
  useEffect(() => {
    if (view !== "search") {
      return undefined;
    }

    const value = identifier.trim();
    if (!meetsThreshold(value)) {
      setMatches([]);
      setSearched(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        const results = await lookupCustomer(value).unwrap();
        const list = Array.isArray(results)
          ? results
          : results
          ? [results]
          : [];
        setMatches(list);
      } catch (err) {
        setMatches([]);
      } finally {
        setSearched(true);
      }
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier, view]);

  const goToCreate = () => {
    prefillCreateForm(identifier.trim());
    setView("create");
  };

  const prefillCreateForm = (value) => {
    setForm({
      ...emptyForm,
      custMobile: isMobile(value) ? value : "",
      custEmail: isEmail(value) ? value : "",
    });
  };

  const attachCustomer = (selected) => {
    dispatch(setSelectedCustomer(selected));
    try {
      sessionStorage.setItem("posCustomer", JSON.stringify(selected));
    } catch {
      // ignore storage failures
    }
    window.dispatchEvent(
      new CustomEvent("pos:customer-changed", { detail: { customer: selected } })
    );
    toast.success("Customer attached to sale", { position: "bottom-right" });
    resetAndClose();
  };

  const handleCreate = async () => {
    if (!form.custName.trim()) {
      toast.error("Customer name is required", { position: "bottom-right" });
      return;
    }
    if (!form.custMobile && !form.custEmail) {
      toast.error("Provide a mobile number or email address", {
        position: "bottom-right",
      });
      return;
    }
    if (form.custEmail && !isEmail(form.custEmail)) {
      toast.error("Email format is invalid", { position: "bottom-right" });
      return;
    }

    try {
      const payload = toCreateCustomerPayload(form);
      const created = await createCustomer(payload).unwrap();
      const customer = created || {
        ...payload,
        custName: form.custName,
      };
      attachCustomer(customer);
      if (purpose !== "reservation") {
        const phone = customer.custMobile || form.custMobile;
        if (phone && String(phone).trim()) {
          dispatch(
            createPhoneOrder({
              customerName: customer.custName || form.custName,
              phone,
            })
          );
        }
        navigate("/restaurant");
      }
    } catch (err) {
      toast.error(err?.data?.message || "Failed to create customer", {
        position: "bottom-right",
      });
    }
  };

  const handleFormChange = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const displayName = (c) =>
    c &&
    (c.custName ||
      `${c.custFirstName || ""} ${c.custLastName || ""}`.trim() ||
      c.custMobile ||
      c.custEmail ||
      "Customer");

  return (
    <Dialog
      fullWidth
      maxWidth={view === "create" ? "sm" : "xs"}
      open={isDialogOpen}
      onClose={resetAndClose}
      PaperProps={{ className: styles.paper, sx: { borderRadius: 3 } }}
    >
      <DialogContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            {view === "create" ? "New Customer" : "Customer"}
          </Typography>
          <IconButton onClick={resetAndClose} size="small">
            <Close />
          </IconButton>
        </Box>

        {view === "search" && (
          <Box>
            <Typography sx={{ color: "#52637f", fontSize: "0.9rem", mb: 2 }}>
              Enter the customer's mobile number or email. Matches appear
              automatically after 4 digits or the first 4 letters.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder="Mobile number or email"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              inputProps={{
                style: {
                  fontSize: "1.5rem",
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonSearchOutlined fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {identifier && (
                      <IconButton
                        size="small"
                        onClick={handleDialpadBackspace}
                        aria-label="Backspace"
                      >
                        <BackspaceOutlined fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => setShowDialpad((prev) => !prev)}
                      color={showDialpad ? "primary" : "default"}
                      aria-label="Toggle dial pad"
                    >
                      <DialpadOutlined fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {showDialpad && (
              <Box
                sx={{
                  mt: 2,
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 1,
                }}
              >
                {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((key) => (
                  <Button
                    key={key}
                    variant="outlined"
                    onClick={() => handleDialpadPress(key)}
                    sx={{
                      py: 1.4,
                      fontSize: "1.15rem",
                      fontWeight: 600,
                      color: "#1f2a44",
                      borderColor: "#e3e8f0",
                    }}
                  >
                    {key}
                  </Button>
                ))}
                <Button
                  variant="outlined"
                  onClick={handleDialpadPress.bind(null, "+")}
                  sx={{
                    py: 1.4,
                    fontSize: "1.15rem",
                    fontWeight: 600,
                    color: "#1f2a44",
                    borderColor: "#e3e8f0",
                  }}
                >
                  +
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleDialpadPress("0")}
                  sx={{
                    py: 1.4,
                    fontSize: "1.15rem",
                    fontWeight: 600,
                    color: "#1f2a44",
                    borderColor: "#e3e8f0",
                  }}
                >
                  0
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDialpadClear}
                  sx={{ py: 1.4, fontSize: "0.85rem", fontWeight: 600 }}
                >
                  Clear
                </Button>
              </Box>
            )}

            <Box sx={{ mt: 2, height: 210, overflowY: "auto" }}>
              {!meetsThreshold(identifier) && (
                <Typography
                  sx={{
                    color: "#94a3b8",
                    fontSize: "0.85rem",
                    textAlign: "center",
                    mt: 3,
                  }}
                >
                  Type at least 4 characters to see matching customers.
                </Typography>
              )}

              {meetsThreshold(identifier) && (
                <>
                  {isSearching && (
                    <Box
                      sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "#52637f",
                      py: 1,
                    }}
                  >
                    <CircularProgress size={16} />
                    <Typography fontSize="0.85rem">Searching...</Typography>
                  </Box>
                )}

                {!isSearching && matches.length > 0 && (
                  <Box
                    sx={{
                      border: "1px solid #e3e8f0",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    {matches.map((c, index) => (
                      <Box
                        key={c.custId || c.customerId || c.id || index}
                        onClick={() => attachCustomer(c)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          px: 1.5,
                          py: 1,
                          cursor: "pointer",
                          borderTop: index === 0 ? "none" : "1px solid #eef1f6",
                          "&:hover": { backgroundColor: "#f5f8ff" },
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={600} noWrap>
                            {displayName(c)}
                          </Typography>
                          <Typography
                            fontSize="0.8rem"
                            sx={{ color: "#52637f" }}
                            noWrap
                          >
                            {[c.custMobile, c.custEmail]
                              .filter(Boolean)
                              .join(" · ") || "—"}
                          </Typography>
                        </Box>
                        <CheckCircleOutline
                          fontSize="small"
                          sx={{ color: "#c3ccdb" }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}

                {!isSearching && searched && matches.length === 0 && (
                  <Box
                    sx={{
                      textAlign: "center",
                      border: "1px dashed #e3e8f0",
                      borderRadius: 2,
                      py: 2,
                      px: 1.5,
                    }}
                  >
                    <SearchOffOutlined sx={{ fontSize: 34, color: "#94a3b8" }} />
                    <Typography fontWeight={600} sx={{ mt: 0.5 }}>
                      No customers found
                    </Typography>
                    <Typography sx={{ mt: 1, fontSize: "0.9rem" }}>
                      <Link
                        component="button"
                        type="button"
                        onClick={goToCreate}
                        sx={{ fontWeight: 600, cursor: "pointer" }}
                      >
                        + Create a new customer
                      </Link>
                    </Typography>
                  </Box>
                )}
                </>
              )}
            </Box>
          </Box>
        )}

        {view === "create" && (
          <Box>
            <Typography sx={{ color: "#52637f", fontSize: "0.9rem", mb: 2 }}>
              Enter the customer details to create a new record.
            </Typography>

            <TextField
              fullWidth
              size="small"
              label="Customer Name"
              value={form.custName}
              onChange={handleFormChange("custName")}
              sx={{ mb: 1.5 }}
            />
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label="Mobile Number"
                value={form.custMobile}
                onChange={handleFormChange("custMobile")}
              />
              <TextField
                fullWidth
                size="small"
                label="Email"
                value={form.custEmail}
                onChange={handleFormChange("custEmail")}
              />
            </Box>

            <Divider sx={{ my: 2 }}>
              <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                ADDRESS (OPTIONAL)
              </Typography>
            </Divider>

            <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label="State"
                value={form.state}
                onChange={handleFormChange("state")}
              />
              <TextField
                fullWidth
                size="small"
                label="Area"
                value={form.area}
                onChange={handleFormChange("area")}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label="Building"
                value={form.building}
                onChange={handleFormChange("building")}
              />
              <TextField
                fullWidth
                size="small"
                label="Floor"
                value={form.floor}
                onChange={handleFormChange("floor")}
              />
              <TextField
                fullWidth
                size="small"
                label="Apartment"
                value={form.apartment}
                onChange={handleFormChange("apartment")}
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              label="Address Line 1"
              value={form.addressLine1}
              onChange={handleFormChange("addressLine1")}
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Address Line 2"
              value={form.addressLine2}
              onChange={handleFormChange("addressLine2")}
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Nearest Landmark"
              value={form.landmark}
              onChange={handleFormChange("landmark")}
              sx={{ mb: 2 }}
            />

            <Divider sx={{ mb: 2 }} />

            <Button
              fullWidth
              variant="contained"
              onClick={handleCreate}
              disabled={isCreating}
              startIcon={
                isCreating ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PersonAddAlt1Outlined />
                )
              }
              sx={{ textTransform: "none", py: 1 }}
            >
              {isCreating ? "Creating..." : "Create & Proceed"}
            </Button>
            <Button
              fullWidth
              onClick={() => setView("search")}
              sx={{ textTransform: "none", mt: 1 }}
            >
              Back to Search
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomerDialog;
